/**
 * A project's History view sessions list (app-clarity language): search, then past sessions as
 * two-line hairline rows with the agent in the icon column. Same data as the classic HistoryTab.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AgentGlyph } from '@viko/transcript-ui'
import { Search, TextSearch } from 'lucide-react'
import { useState } from 'react'
import { Ago, SkeletonRows } from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { runHref } from '../lib/ia.js'
import type { HistoryRun, Project, RunSummary } from '../lib/types.js'
import { Snippet } from './projects.js'
import './project-views.css'

export function ProjectSessions({ project }: { project: Project }) {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const history = useQuery({
    queryKey: ['history', b.artifactAccess.accountId, project.id],
    queryFn: () => b.api.projectHistory(project.id, { limit: 100 }),
    retry: false,
  })
  const [q, setQ] = useState('')
  const [term, setTerm] = useState('')
  const hits = useQuery({
    queryKey: ['search', b.artifactAccess.accountId, term, project.id],
    queryFn: () => b.api.search(term, { projectId: project.id, limit: 50 }),
    enabled: term.length >= 2,
    retry: false,
  })
  const open = (r: HistoryRun) => {
    qc.setQueryData<RunSummary>(['runinfo', r.runId], {
      ...r,
      agent: r.agent ?? 'claude',
      projectName: project.name,
      updatedAt: r.lastActivityAt,
    } as RunSummary)
    void nav({ href: runHref(r.runId, project.id) })
  }
  const runs = history.data?.runs ?? []
  const byId = new Map(runs.map((r) => [r.runId, r]))
  return (
    <>
      <section className="pv-sec" aria-labelledby="pv-sessions">
        <h2 className="pv-h" id="pv-sessions">
          Sessions {history.isSuccess ? <span className="pv-h__count">{runs.length}</span> : null}
        </h2>
        <form
          className="pv-search"
          role="search"
          aria-label={`Search ${project.name} transcripts`}
          onSubmit={(e) => {
            e.preventDefault()
            setTerm(q.trim())
          }}
        >
          <Search size={16} aria-hidden="true" className="pv-search__icon" />
          <input
            className="pv-search__input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search every session of this project"
            aria-label="Search transcripts"
            enterKeyHint="search"
          />
          {q.trim().length >= 2 ? (
            <button type="submit" className="pv-btn">
              Search
            </button>
          ) : null}
        </form>
        {term.length >= 2 ? (
          <section aria-label="Search results" className="pv-sec">
            <p className="pv-note">
              {hits.isPending
                ? 'Searching…'
                : `${hits.data?.length ?? 0} match${hits.data?.length === 1 ? '' : 'es'} for “${term}”`}
            </p>
            <ul className="pv-rows">
              {(hits.data ?? []).map((h) => {
                const runId = h.runId ?? h.run_id
                const hr = byId.get(runId)
                return (
                  <li key={`${runId}:${h.seq}`}>
                    <button
                      type="button"
                      className="pv-row pv-row--btn"
                      onClick={() =>
                        hr ? open(hr) : void nav({ href: runHref(runId, project.id, h.seq) })
                      }
                    >
                      <span className="pv-row__icon">
                        <TextSearch size={16} aria-hidden="true" />
                      </span>
                      <span className="pv-row__main">
                        <span className="pv-row__title">
                          <Snippet text={h.snippet} />
                        </span>
                        <span className="pv-row__meta">
                          {h.title ?? runId}
                          {h.source && h.source !== 'viko' ? ` · ${h.source}` : ''}
                        </span>
                      </span>
                      <span className="pv-row__aside">{h.ts ? <Ago ts={h.ts} /> : null}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </section>
        ) : null}
        {history.isPending ? <SkeletonRows n={3} /> : null}
        {history.isError ? (
          <p className="pv-empty">Sessions didn’t load. They come back when the hub answers.</p>
        ) : null}
        {history.isSuccess && !runs.length ? (
          <p className="pv-empty">
            No past sessions yet. Turn on history sharing below, under Settings, to bring in recent
            conversations.
          </p>
        ) : null}
        {runs.length ? (
          <section aria-label="Past sessions">
            <ul className="pv-rows">
              {runs.map((r) => (
                <li key={r.runId}>
                  <button type="button" className="pv-row pv-row--btn" onClick={() => open(r)}>
                    <span className="pv-row__icon">
                      <AgentGlyph agent={r.agent ?? 'claude'} size={16} />
                    </span>
                    <span className="pv-row__main">
                      <span className="pv-row__title">{r.title ?? r.runId}</span>
                      <span className="pv-row__meta">
                        {r.typingElsewhere ? (
                          <span className="pv-word-wait">Live in a terminal</span>
                        ) : r.source === 'adopted' ? (
                          'Terminal session'
                        ) : (
                          'Imported'
                        )}
                        {r.gitBranch ? (
                          <>
                            {' · '}
                            <code className="pv-mono">{r.gitBranch}</code>
                          </>
                        ) : null}
                      </span>
                    </span>
                    <span className="pv-row__aside">
                      <Ago ts={r.lastActivityAt} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </section>
    </>
  )
}
